import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Skeleton, Typography } from 'antd'

import { letterTemplateVariablesStore } from '../Stores/LetterTemplateVariablesStore'

import type { LetterTemplateFormValues } from '../Contracts/LetterTemplate'

interface LetterTemplateVariablesProps {
    formContext: { values?: LetterTemplateFormValues } | undefined
}

/**
 * Перелік змінних обраного провайдера під редактором шаблону (`SPEC-013` §9.1;
 * легасі `LetterTemplateVariables.jsx`): рядки «`${ключ}` — підпис», під час
 * завантаження — `Skeleton`. Без провайдера — нічого.
 *
 * Окремий observer: провайдер — значення, яке редагують, а шаблон форми
 * значень не читає (`SPEC-002` §4.2).
 */
export const LetterTemplateVariables = observer(({ formContext }: LetterTemplateVariablesProps) => {
    const raw = formContext?.values?.provider
    const provider = typeof raw === 'string' ? raw : ''
    const store = letterTemplateVariablesStore

    useEffect(() => {
        if (provider !== '') void store.load(provider)
    }, [provider, store])

    if (provider === '') return null

    const variables = store.variables(provider)
    const error = store.error(provider)

    return (
        <div data-testid="letter-template-variables">
            <Typography.Text strong>Змінні</Typography.Text>
            {error ? (
                <Alert type="error" showIcon message={error} style={{ marginTop: 8 }} />
            ) : variables === null || store.isLoading(provider) ? (
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
            ) : (
                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    {variables.map((variable) => (
                        <li key={variable.key}>
                            <Typography.Text code copyable={{ text: variable.key }}>
                                {variable.key}
                            </Typography.Text>{' '}
                            — {variable.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
})
